import { useEffect, useState } from 'react';
import { api } from './api';
import { paragraphs } from './siteContent';
import type { ArticleItem } from '@components/pages/ResourceSections';

/** What the record adds to a listing entry. */
interface Body {
    slug: string;
    bodyHtml?: string;
    body?: string[];
    quote?: string;
}

/** The record as the API returns it: a story carries `article`, an article `bodyHtml`. */
interface FetchedRecord {
    bodyHtml?: string;
    body?: string[];
    quote?: string;
    article?: string;
}

/**
 * Fills in an article's body.
 *
 * The listing feeds carry no bodies: 145 articles came to 1.3 MB of the 1.66 MB
 * the site downloaded before it could paint, and a list of cards shows none of
 * it. So the page that renders one asks for that record on its own.
 *
 * The fetched body is kept beside the item rather than in place of it. Holding a
 * copy of the whole item in state meant that, in the render where the feed
 * landed, the pool had the article while this still returned nothing — and the
 * page, seeing no article and hydration finished, redirected to the 404.
 */
export function useArticleBody(
    item: ArticleItem | undefined,
    collection: 'articles' | 'stories',
): { item: ArticleItem | undefined; bodyPending: boolean } {
    const slug = item?.slug;
    const hasBody = !!item?.bodyHtml || !!item?.body?.length;
    const [fetched, setFetched] = useState<Body | null>(null);
    /** Set when the request cannot be answered, so the page stops holding space for it. */
    const [failedFor, setFailedFor] = useState<string | null>(null);

    useEffect(() => {
        if (!slug || hasBody) return;

        let live = true;
        void api
            .get<{ record: FetchedRecord }>(`/site/resources/${collection}/${encodeURIComponent(slug)}`)
            .then(({ record }) => {
                // Only the body fields, keyed by the slug this page routes on.
                // Spreading the record would bring its own `slug` with it — the
                // CMS one, `/blog/…`, which never matches the routed segment.
                if (!live || !record) return;
                // A story keeps its text in `article`, as one string of paragraphs,
                // where an article uses `bodyHtml`. Split it the same way the pool
                // mapper does, or a story fetched here would come back empty.
                const story = collection === 'stories' ? paragraphs(record.article, []) : [];
                setFetched({
                    slug,
                    bodyHtml: record.bodyHtml,
                    body: story.length ? story : record.body,
                    quote: record.quote,
                });
            })
            .catch(() => {
                /* The page keeps its excerpt and picture; better than an error. */
                if (live) setFailedFor(slug);
            });
        return () => {
            live = false;
        };
    }, [slug, collection, hasBody]);

    if (!item) return { item: undefined, bodyPending: false };
    // Only this article's body, never the one fetched for the page before it.
    if (hasBody || fetched?.slug !== item.slug) {
        return { item, bodyPending: !hasBody && failedFor !== item.slug };
    }
    return {
        item: {
            ...item,
            bodyHtml: fetched.bodyHtml || item.bodyHtml,
            body: fetched.body?.length ? fetched.body : item.body,
            quote: fetched.quote || item.quote,
        },
        bodyPending: false,
    };
}
